<template>
  <div class="shop-page-root">
    <AppNavbar />

    <div class="hero-full-bleed-row">
      <header class="shop-hero-banner">
        <div class="hero-glow one"></div>
        <div class="hero-glow two"></div>

        <div class="banner-inner-container">
          <nav class="breadcrumb-nav">
            <a href="/" class="crumb-link">Home</a>
            <span class="separator">/</span>
            <span class="active-node">Shop</span>
            <template v-if="selectedCategoryLabel !== 'All Collections'">
              <span class="separator">/</span>
              <span class="current-leaf">{{ selectedCategoryLabel }}</span>
            </template>
          </nav>

          <h1 class="page-title-main">{{ selectedCategoryLabel }}</h1>
          <p class="page-subtitle">
            Premium Collections Crafted to Perfection.
          </p>
        </div>
      </header>
    </div>

    <div class="content-full-bleed-row">
      <main class="container shop-content">
        <p v-if="isBootstrapping" class="status-text centered">
          Loading shop data...
        </p>
        <p v-else-if="errorMessage" class="error-text centered">
          {{ errorMessage }}
        </p>

        <template v-else>
          <section class="shop-layout">
            <!-- MOBILE CATEGORY TOGGLE BAR -->
            <div class="mobile-category-bar" @click="toggleMobileCategories">
              <span class="mobile-cat-label">
                <i class="ri-layout-list-line"></i>
                Browse Categories
              </span>
              <span class="mobile-cat-selected">{{
                selectedCategoryLabel
              }}</span>
              <i
                class="ri-arrow-down-s-line mobile-chevron"
                :class="{ open: mobileMenuOpen }"
              ></i>
            </div>

            <!-- CATEGORY PANEL -->
            <aside
              class="category-panel section-card"
              :class="{ 'mobile-open': mobileMenuOpen }"
            >
              <div class="section-heading-row">
                <h2 class="section-title">Categories</h2>
                <span class="count-pill">{{ categories.length }}</span>
              </div>

              <p v-if="!categories.length" class="status-text">
                No categories found.
              </p>

              <div v-else class="category-list">
                <!-- ALL COLLECTIONS -->
                <a
                  href="#"
                  class="category-chip root-chip"
                  :class="{ active: selectedCategoryId === '' }"
                  @click.prevent="clearCategorySelection"
                >
                  <span class="category-name">All Collections</span>
                </a>

                <!-- ROOT CATEGORIES -->
                <div
                  v-for="(category, index) in categories"
                  :key="getCategoryId(category) || index"
                  class="category-group"
                >
                  <!-- ROOT CHIP -->
                  <div
                    class="category-chip root-chip"
                    :class="{
                      active: selectedCategoryId === getCategoryId(category),
                    }"
                    @click="toggleRootCategory(category)"
                  >
                    <span class="category-name">{{
                      getCategoryName(category, index)
                    }}</span>
                    <div class="chip-right">
                      <span
                        v-if="getCategoryTotalItemCount(category) !== null"
                        class="category-total-count-badge"
                        >{{ getCategoryTotalItemCount(category) }}</span
                      >
                      <i
                        v-if="getCategoryChildren(category).length"
                        class="ri-arrow-down-s-line tree-chevron"
                        :class="{
                          open: openRootIds.has(getCategoryId(category)),
                        }"
                      ></i>
                    </div>
                  </div>

                  <!-- LEVEL 1 CHILDREN -->
                  <transition name="tree-expand">
                    <div
                      v-if="
                        getCategoryChildren(category).length &&
                        openRootIds.has(getCategoryId(category))
                      "
                      class="children-block"
                    >
                      <div
                        v-for="(child, childIndex) in getCategoryChildren(
                          category,
                        )"
                        :key="getCategoryId(child) || childIndex"
                        class="category-group"
                      >
                        <!-- LEVEL 1 CHIP -->
                        <div
                          class="category-chip level-1-chip"
                          :class="{
                            active: selectedCategoryId === getCategoryId(child),
                          }"
                          @click="toggleChildCategory(child)"
                        >
                          <div class="name-wrapper">
                            <span class="level-indicator">›</span>
                            <span class="category-name">{{
                              getCategoryName(child, childIndex)
                            }}</span>
                          </div>
                          <div class="chip-right">
                            <span
                              v-if="getCategoryTotalItemCount(child) !== null"
                              class="category-total-count-badge"
                              >{{ getCategoryTotalItemCount(child) }}</span
                            >
                            <i
                              v-if="getCategoryChildren(child).length"
                              class="ri-arrow-down-s-line tree-chevron"
                              :class="{
                                open: openChildIds.has(getCategoryId(child)),
                              }"
                            ></i>
                          </div>
                        </div>

                        <!-- LEVEL 2 CHILDREN -->
                        <transition name="tree-expand">
                          <div
                            v-if="
                              getCategoryChildren(child).length &&
                              openChildIds.has(getCategoryId(child))
                            "
                            class="children-block"
                          >
                            <div
                              v-for="(
                                grandchild, grandIndex
                              ) in getCategoryChildren(child)"
                              :key="getCategoryId(grandchild) || grandIndex"
                              class="category-chip level-2-chip"
                              :class="{
                                active:
                                  selectedCategoryId ===
                                  getCategoryId(grandchild),
                              }"
                              @click="handleCategoryClick(grandchild)"
                            >
                              <div class="name-wrapper">
                                <span class="level-indicator muted">›</span>
                                <span class="category-name">{{
                                  getCategoryName(grandchild, grandIndex)
                                }}</span>
                              </div>
                              <span
                                v-if="
                                  getCategoryTotalItemCount(grandchild) !== null
                                "
                                class="category-total-count-badge"
                                >{{
                                  getCategoryTotalItemCount(grandchild)
                                }}</span
                              >
                            </div>
                          </div>
                        </transition>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </aside>

            <section class="products-panel">
              <div class="catalog-header section-card">
                <div class="meta-info">
                  <p class="active-category">
                    Showing: <strong>{{ selectedCategoryLabel }}</strong>
                  </p>
                  <span class="count-summary"
                    >{{ filteredProducts.length }} products available</span
                  >
                </div>

                <div class="control-actions">
                  <div class="sort-select-wrap">
                    <label class="sort-label" for="shopSortSelect"
                      >Sort By</label
                    >
                    <select
                      id="shopSortSelect"
                      class="sort-select"
                      :value="sortOption"
                      @change="onSortChange"
                    >
                      <option value="default">Featured</option>
                      <option value="name-asc">Name: A–Z</option>
                      <option value="name-desc">Name: Z–A</option>
                      <option value="price-asc">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                    </select>
                  </div>

                  <button
                    class="filter-toggle-btn"
                    :class="{ active: showFiltersPanel }"
                    type="button"
                    @click="toggleFilterPanel"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polygon
                        points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"
                      ></polygon>
                    </svg>
                    <span>{{
                      showFiltersPanel ? "Hide Filters" : "Filters"
                    }}</span>
                  </button>

                  <div class="layout-toggle-group">
                    <button
                      type="button"
                      class="view-btn"
                      :class="{ active: currentViewLayout === 'grid' }"
                      @click="setViewLayout('grid')"
                      aria-label="Grid View"
                    >
                      <i class="ri-grid-fill"></i>
                    </button>
                    <button
                      type="button"
                      class="view-btn"
                      :class="{ active: currentViewLayout === 'list' }"
                      @click="setViewLayout('list')"
                      aria-label="List View"
                    >
                      <i class="ri-list-unordered"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-if="showFiltersPanel"
                class="filter-panel section-card-nested"
              >
                <h3 class="mini-title">Refine Results</h3>

                <p v-if="isFilterLoading" class="status-text">
                  Loading filters...
                </p>
                <p
                  v-else-if="!availableFilters.length"
                  class="status-text mini"
                >
                  No filters available for this category.
                </p>

                <div v-else class="filter-groups">
                  <div
                    v-for="(filter, filterIndex) in availableFilters"
                    :key="`${
                      filter.key || filter.name || 'filter'
                    }-${filterIndex}`"
                    class="filter-group"
                  >
                    <h4 class="filter-group-title">
                      {{ getFilterDisplayName(filter, filterIndex) }}
                    </h4>
                    <div class="filter-options">
                      <label
                        v-for="(option, optionIndex) in normalizeFilterValues(
                          filter,
                        )"
                        :key="`${filter.key || filter.name}-${
                          option.value
                        }-${optionIndex}`"
                        class="filter-option"
                      >
                        <input
                          type="checkbox"
                          class="custom-checkbox"
                          :checked="
                            isFilterOptionSelected(filter.key, option.value)
                          "
                          @change="
                            onFilterOptionChange(
                              filter.key,
                              option.value,
                              $event,
                            )
                          "
                        />
                        <span class="checkbox-label-text">{{
                          option.display_name
                        }}</span>
                      </label>
                    </div>
                  </div>

                  <div class="filter-actions-footer">
                    <button
                      type="button"
                      class="apply-filter-btn"
                      @click="applySelectedFilters"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      class="clear-filter-btn"
                      @click="clearSelectedFilters"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              <p v-if="isProductsLoading" class="status-text centered">
                Loading products...
              </p>
              <p
                v-else-if="!filteredProducts.length"
                class="status-text catalog-empty section-card"
              >
                No products found for the selected category.
              </p>

              <div
                v-else
                :class="[
                  'product-cards-container',
                  `view-${currentViewLayout}`,
                ]"
              >
                <article
                  v-for="(product, index) in sortedProducts"
                  :key="getProductId(product) || index"
                  class="premium-product-card"
                  @click="handleProductClick(product)"
                >
                  <div class="product-image-wrap">
                    <span v-if="product.isOnSale" class="badge-sale">Sale</span>

                    <img
                      v-if="getProductImageUrl(product)"
                      :src="getProductImageUrl(product)"
                      :alt="getProductName(product, index)"
                      class="product-card-image"
                      loading="lazy"
                    />
                    <div v-else class="product-image-fallback">
                      <span>No Image Available</span>
                    </div>
                  </div>

                  <div class="product-card-body">
                    <div class="content-text-group">
                      <h3 class="product-name">
                        {{ getProductName(product, index) }}
                      </h3>
                      <p class="product-description">
                        {{ getProductDescription(product) }}
                      </p>
                      <div class="product-prices">
                        <span class="price-chip net">{{
                          getNetPrice(product)
                        }}</span>
                        <span
                          v-if="shouldShowMrp(product)"
                          class="price-chip mrp"
                          >{{ getMrpPrice(product) }}</span
                        >
                      </div>
                    </div>
                    <!-- 
                    <div class="action-btn-wrapper">
                      <router-link
                        :to="{
                          name: 'product-detail',
                          params: { productId: getProductId(product) },
                        }"
                        class="view-details-link"
                        @click.stop
                      >
                        View Details
                      </router-link>
                      <button
                        type="button"
                        class="brand-movement-add-btn"
                        :class="{ added: isProductAdded(product) }"
                        :disabled="
                          isProductAdded(product) || isAddInProgress(product)
                        "
                        @click.stop="handleAddToCart(product)"
                      >
                        {{
                          isProductAdded(product)
                            ? "Added"
                            : isAddInProgress(product)
                            ? "Adding..."
                            : "Add to Cart"
                        }}
                      </button>
                    </div> -->
                  </div>
                </article>
              </div>
            </section>
          </section>
        </template>
      </main>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  checkTenant,
  getCategoriesByTenant,
  getFiltersByTenantAndStore,
  getProductsByTenantAndStore,
} from "rdep-ecom-sdk";
import { useAppStore } from "../../stores/useAppStore.js";
// import { useCartStore } from "../../stores/useCartStore.js";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const appStore = useAppStore();
// const cartStore = useCartStore();
const route = useRoute();
const router = useRouter();

// --- STATE ---
const categories = ref([]);
const availableFilters = ref([]);
const products = ref([]);

const selectedCategoryId = ref("");
const showFiltersPanel = ref(false);
const selectedFilters = ref({});
const currentViewLayout = ref("grid");
const sortOption = ref("default");

const isBootstrapping = ref(false);
const isProductsLoading = ref(false);
const isFilterLoading = ref(false);

const errorMessage = ref("");
// const addingByKey = ref({});

const resolvedTenantId = ref("");
const resolvedStoreId = ref("");

// --- NEW: category collapse state ---
const openRootIds = ref(new Set());
const openChildIds = ref(new Set());
const mobileMenuOpen = ref(false);

// --- RANGE-BASED FILTER CONFIG ---
// Filters below this raw-unique-value count are left as-is (checkbox list).
// Above it, and if values are numeric, they're bucketed into ranges.
const RANGE_BUCKET_THRESHOLD = 6;
const MAX_RANGE_BUCKETS = 5;

// --- UTILITIES ---
function getItemName(item, fallback = "") {
  if (!item) return fallback;
  return (
    item.name ||
    item.title ||
    item.categoryName ||
    item.subCategoryName ||
    item.productName ||
    fallback
  );
}

function getCategoryId(category) {
  if (!category) return "";
  return String(
    category.categoryId || category.id || category.categoryID || "",
  ).trim();
}

function getCategoryName(category, index = 0) {
  return getItemName(category, `Category ${index + 1}`);
}

function getCategoryTotalItemCount(category) {
  if (!category) return null;
  const rawCount =
    category.totalItemCount ??
    category.total_item_count ??
    category.itemCount ??
    null;
  if (rawCount === null || rawCount === undefined || rawCount === "")
    return null;
  return rawCount;
}

function getCategoryChildren(category) {
  if (!category) return [];
  if (Array.isArray(category.subcategories)) return category.subcategories;
  if (Array.isArray(category.subCategories)) return category.subCategories;
  return [];
}

function flattenCategoryTree(categoryList, depth = 0) {
  const result = [];
  for (const category of categoryList || []) {
    result.push({ category, depth });
    const children = getCategoryChildren(category);
    if (children.length)
      result.push(...flattenCategoryTree(children, depth + 1));
  }
  return result;
}

function collectCategoryScopeIds(category, idSet = new Set()) {
  const categoryId = getCategoryId(category);
  if (categoryId) idSet.add(String(categoryId));
  const children = getCategoryChildren(category);
  for (const child of children) collectCategoryScopeIds(child, idSet);
  return idSet;
}

function getProductCategoryId(product) {
  if (!product) return "";
  return String(
    product.categoryId ||
      product.category_id ||
      product.categoryID ||
      product.category?.id ||
      product.category?.categoryId ||
      "",
  ).trim();
}

function findCategoryById(categoryList, targetCategoryId) {
  const normalizedTarget = String(targetCategoryId || "").trim();
  if (!normalizedTarget) return null;

  for (const category of categoryList || []) {
    if (getCategoryId(category) === normalizedTarget) {
      return category;
    }

    const childMatch = findCategoryById(
      getCategoryChildren(category),
      normalizedTarget,
    );
    if (childMatch) {
      return childMatch;
    }
  }

  return null;
}

function getProductId(product) {
  if (!product) return "";
  return String(
    product.productId || product.id || product.productID || product.sku || "",
  );
}

// function getProductSku(product) {
//   if (!product) return "";
//   return String(product.sku || "");
// }

// function getProductCartKey(product) {
//   return getProductId(product) || getProductSku(product);
// }

function getProductName(product, index = 0) {
  return getItemName(product, `Product ${index + 1}`);
}

function getProductDescription(product) {
  return product?.sku || "Description unavailable";
}

function getProductImageUrl(product) {
  if (!product) return "";

  const imageSources = [
    product.files,
    product.productFiles,
    product.images,
    product.product?.files,
    product.product?.productFiles,
    product.product?.images,
  ];

  for (const source of imageSources) {
    if (!Array.isArray(source) || !source.length) continue;
    const firstImage = source.find((file) =>
      Boolean(file?.path || file?.url || file?.imageUrl || file?.image),
    );
    if (firstImage) {
      return (
        firstImage.path ||
        firstImage.url ||
        firstImage.imageUrl ||
        firstImage.image ||
        ""
      );
    }
  }

  return (
    product.filePath ||
    product.image ||
    product.imageUrl ||
    product.thumbnail ||
    ""
  );
}

function getListPrice(product) {
  return (
    product?.listPrice ??
    product?.list_price ??
    product?.product?.listPrice ??
    product?.product?.list_price ??
    null
  );
}

// --- CURRENCY FORMATTING (₹ + Indian comma grouping) ---
function formatNumericValue(value) {
  if (value === undefined || value === null || value === "") return null;

  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;

  if (normalizedValue === "") return null;

  const parsedNumber = Number(normalizedValue);
  if (!Number.isFinite(parsedNumber)) return String(value);

  // "en-IN" locale applies Indian digit grouping (e.g. 12,34,567) automatically.
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 20,
  }).format(parsedNumber);
}

function formatRs(value, product = {}) {
  const effectiveValue =
    value === undefined || value === null || value === ""
      ? getListPrice(product)
      : value;

  if (
    effectiveValue === undefined ||
    effectiveValue === null ||
    effectiveValue === ""
  ) {
    return "N/A";
  }

  const formattedValue = formatNumericValue(effectiveValue);
  return `₹${formattedValue ?? effectiveValue}`;
}

function getMrpPrice(product) {
  return formatRs(product?.mrp, product);
}

function parseComparableAmount(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  if (normalizedValue === "") return null;
  const parsedNumber = Number(normalizedValue);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
}

function getNetPriceRaw(product) {
  return (
    product?.net_price ??
    product?.netPrice ??
    product?.listPrice ??
    product?.list_price ??
    product?.product?.net_price ??
    product?.product?.netPrice ??
    product?.product?.listPrice ??
    product?.product?.list_price
  );
}

function getNetPrice(product) {
  return formatRs(getNetPriceRaw(product), product);
}

function shouldShowMrp(product) {
  const mrpValue = parseComparableAmount(product?.mrp ?? product?.product?.mrp);
  if (mrpValue === null) return false;

  const netPriceValue = parseComparableAmount(getNetPriceRaw(product));
  if (netPriceValue === null) return true;

  return mrpValue !== netPriceValue;
}

// Numeric (unformatted) net price, used for price sorting.
function getRawNetPriceValue(product) {
  const raw = getNetPriceRaw(product);
  if (raw === undefined || raw === null || raw === "") return null;
  const normalized =
    typeof raw === "string" ? raw.replace(/,/g, "").trim() : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function getFilterDisplayName(filter, index = 0) {
  return (
    filter?.display_name || filter?.name || filter?.key || `Filter ${index + 1}`
  );
}

// --- RANGE BUCKETING HELPERS ---

// Pulls the best available text for a raw filter value, preferring
// display_name first since that's where unit text (e.g. "stones", "Gms")
// actually lives; falls back to value/label/name.
function extractFilterValueText(rawValue) {
  if (rawValue && typeof rawValue === "object") {
    return (
      rawValue.display_name ??
      rawValue.value ??
      rawValue.label ??
      rawValue.name ??
      ""
    );
  }
  return rawValue;
}

// Extracts a leading numeric portion + trailing unit text from a raw filter value.
// e.g. "0.300 Gms" -> { num: 0.3, unit: "Gms" }, "31 stones" -> { num: 31, unit: "stones" }
function parseNumericWithUnit(rawValue) {
  const text = String(rawValue ?? "").trim();
  const match = text.match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/);
  if (!match) return null;
  const num = Number(match[1]);
  if (!Number.isFinite(num)) return null;
  const unit = String(match[2] || "").trim();
  return { num, unit };
}

function roundToStep(value, step) {
  return Math.round(value / step) * step;
}

// Picks a "nice" bucket step size based on the data's min/max spread,
// targeting roughly MAX_RANGE_BUCKETS buckets.
function pickBucketStep(min, max) {
  const range = Math.max(max - min, 0.0001);
  const rawStep = range / MAX_RANGE_BUCKETS;

  // Candidate "nice" steps, smallest-first.
  const niceSteps = [
    0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000,
  ];

  for (const step of niceSteps) {
    if (step >= rawStep) return step;
  }
  return niceSteps[niceSteps.length - 1];
}

function formatBucketNumber(value, unit) {
  const isWeightLike = /g$|gms?$/i.test(unit || "");
  const isCaratLike = /ct/i.test(unit || "");
  const decimals = isWeightLike || isCaratLike ? 2 : value % 1 === 0 ? 0 : 2;
  return Number(value.toFixed(decimals)).toString();
}

// Builds range-bucket options ({display_name, value}) from a filter's raw values.
// `value` for a bucket is a JSON-encoded [min, max] pair (as a string) so it can
// travel through the existing string-based selection/query plumbing untouched.
function buildRangeBuckets(filter) {
  const rawValues = Array.isArray(filter?.values) ? filter.values : [];

  const parsed = rawValues
    .map((rawValue) => parseNumericWithUnit(extractFilterValueText(rawValue)))
    .filter(Boolean);

  if (!parsed.length) return null;

  const unit = parsed.find((entry) => entry.unit)?.unit || "";
  const numbers = parsed.map((entry) => entry.num);
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);

  if (min === max) return null;

  const step = pickBucketStep(min, max);
  const bucketStart = Math.floor(min / step) * step;
  const bucketEnd = Math.ceil(max / step) * step;

  const buckets = [];
  for (let start = bucketStart; start < bucketEnd; start += step) {
    const end = start + step;
    const rangeMin = roundToStep(start, step);
    const rangeMax = roundToStep(end, step);

    const label = unit
      ? `${formatBucketNumber(rangeMin, unit)}–${formatBucketNumber(
          rangeMax,
          unit,
        )} ${unit}`
      : `${formatBucketNumber(rangeMin, unit)}–${formatBucketNumber(
          rangeMax,
          unit,
        )}`;

    buckets.push({
      display_name: label,
      value: JSON.stringify({ min: rangeMin, max: rangeMax }),
    });

    if (buckets.length >= MAX_RANGE_BUCKETS) break;
  }

  return buckets.length ? buckets : null;
}

// Decides whether a filter's raw values should be shown as ranges.
function shouldUseRangeBuckets(filter) {
  const rawValues = Array.isArray(filter?.values) ? filter.values : [];
  if (rawValues.length <= RANGE_BUCKET_THRESHOLD) return false;

  const parsedCount = rawValues.filter((rawValue) =>
    Boolean(parseNumericWithUnit(extractFilterValueText(rawValue))),
  ).length;

  // Consider it "numeric" if the vast majority of raw values parse as numbers.
  return parsedCount / rawValues.length >= 0.7;
}

function normalizeFilterValues(filter) {
  if (!Array.isArray(filter?.values)) return [];

  if (shouldUseRangeBuckets(filter)) {
    const buckets = buildRangeBuckets(filter);
    if (buckets) return buckets;
  }

  return filter.values.map((value, index) => {
    if (value && typeof value === "object") {
      const normalizedValue = String(value.value ?? value.display_name ?? "");
      return {
        display_name:
          value.display_name || value.name || value.label || normalizedValue,
        value: normalizedValue,
      };
    }
    return {
      display_name: String(value ?? `Option ${index + 1}`),
      value: String(value ?? ""),
    };
  });
}

// --- FILTER ACTIONS ---
function toggleFilterPanel() {
  showFiltersPanel.value = !showFiltersPanel.value;
}
function setViewLayout(layoutName) {
  currentViewLayout.value = layoutName;
}

function onSortChange(event) {
  sortOption.value = event.target.value;
}

function isFilterOptionSelected(filterKey, optionValue) {
  const key = String(filterKey || "");
  const value = String(optionValue || "");
  if (!key || !value) return false;
  return (selectedFilters.value[key] || []).includes(value);
}

function onFilterOptionChange(filterKey, optionValue, event) {
  const key = String(filterKey || "");
  const value = String(optionValue || "");
  if (!key || !value) return;
  const selectedValues = [...(selectedFilters.value[key] || [])];
  const selectedIndex = selectedValues.indexOf(value);
  if (event?.target?.checked && selectedIndex === -1)
    selectedValues.push(value);
  if (!event?.target?.checked && selectedIndex > -1)
    selectedValues.splice(selectedIndex, 1);
  selectedFilters.value = { ...selectedFilters.value, [key]: selectedValues };
}

// Selected filter values may be either raw strings (normal filters) or
// JSON-encoded {min,max} range buckets (numeric filters). This expands
// range-bucket selections back into the set of raw values they cover,
// so the payload sent to the backend still matches actual product data.
function expandSelectedValuesForKey(key, selectedValues) {
  const matchingFilter = availableFilters.value.find(
    (filter) => String(filter.key || filter.name) === key,
  );
  if (!matchingFilter) return selectedValues;

  const rawValues = Array.isArray(matchingFilter.values)
    ? matchingFilter.values
    : [];

  const expanded = new Set();

  for (const selectedValue of selectedValues) {
    let rangeBucket = null;
    try {
      const parsedCandidate = JSON.parse(selectedValue);
      if (
        parsedCandidate &&
        typeof parsedCandidate === "object" &&
        "min" in parsedCandidate &&
        "max" in parsedCandidate
      ) {
        rangeBucket = parsedCandidate;
      }
    } catch (error) {
      rangeBucket = null;
    }

    if (!rangeBucket) {
      expanded.add(selectedValue);
      continue;
    }

    for (const rawValue of rawValues) {
      const value = extractFilterValueText(rawValue);
      const parsedNumeric = parseNumericWithUnit(value);
      if (!parsedNumeric) continue;
      if (
        parsedNumeric.num >= rangeBucket.min &&
        parsedNumeric.num < rangeBucket.max
      ) {
        expanded.add(String(value));
      }
    }
  }

  return Array.from(expanded);
}

function getSelectedFiltersPayload() {
  return Object.entries(selectedFilters.value)
    .filter(([, values]) => Array.isArray(values) && values.length > 0)
    .map(([key, values]) => ({
      key,
      values: expandSelectedValuesForKey(key, values),
    }))
    .filter((entry) => entry.values.length > 0);
}

// --- NEW: category toggle handlers ---
function toggleMobileCategories() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

async function toggleRootCategory(category) {
  const id = getCategoryId(category);

  const newSet = new Set(openRootIds.value);
  if (newSet.has(id)) {
    newSet.delete(id);
  } else {
    newSet.add(id);
  }
  openRootIds.value = newSet;

  if (selectedCategoryId.value === id) return;

  await handleCategoryClick(category);
}

async function toggleChildCategory(child) {
  const id = getCategoryId(child);

  if (getCategoryChildren(child).length) {
    const newSet = new Set(openChildIds.value);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    openChildIds.value = newSet;
  }

  if (selectedCategoryId.value === id) return;

  await handleCategoryClick(child);
}

// --- COMPUTED ---
const flatCategoryMenu = computed(() =>
  flattenCategoryTree(categories.value, 0),
);

const selectedCategoryScopeIds = computed(() => {
  if (!selectedCategoryId.value) return new Set();
  const selectedEntry = flatCategoryMenu.value.find(
    (entry) =>
      String(getCategoryId(entry.category)) ===
      String(selectedCategoryId.value),
  );
  if (!selectedEntry) return new Set([String(selectedCategoryId.value)]);
  return collectCategoryScopeIds(selectedEntry.category, new Set());
});

const filteredProducts = computed(() => {
  if (!selectedCategoryId.value) return products.value;
  return products.value.filter((product) => {
    const productCategoryId = getProductCategoryId(product);
    return selectedCategoryScopeIds.value.has(String(productCategoryId));
  });
});

// Applies the currently selected sort option on top of filteredProducts.
const sortedProducts = computed(() => {
  const list = [...filteredProducts.value];

  switch (sortOption.value) {
    case "name-asc":
      return list.sort((a, b) =>
        getProductName(a).localeCompare(getProductName(b), undefined, {
          sensitivity: "base",
        }),
      );

    case "name-desc":
      return list.sort((a, b) =>
        getProductName(b).localeCompare(getProductName(a), undefined, {
          sensitivity: "base",
        }),
      );

    case "price-asc":
      return list.sort((a, b) => {
        const priceA = getRawNetPriceValue(a);
        const priceB = getRawNetPriceValue(b);
        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return priceA - priceB;
      });

    case "price-desc":
      return list.sort((a, b) => {
        const priceA = getRawNetPriceValue(a);
        const priceB = getRawNetPriceValue(b);
        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return priceB - priceA;
      });

    default:
      return list;
  }
});

const selectedCategoryLabel = computed(() => {
  if (!selectedCategoryId.value) return "All Collections";
  const selectedEntry = flatCategoryMenu.value.find(
    (entry) =>
      String(getCategoryId(entry.category)) ===
      String(selectedCategoryId.value),
  );
  if (!selectedEntry) return "All Collections";
  return getCategoryName(selectedEntry.category);
});

// const cartItemKeys = computed(() => {
//   return new Set(
//     (cartStore.items || [])
//       .map((item) => String(item?.key || item?.productId || item?.sku || ""))
//       .filter(Boolean),
//   );
// });

// function isProductAdded(product) {
//   const key = getProductCartKey(product);
//   return key ? cartItemKeys.value.has(String(key)) : false;
// }

// function isAddInProgress(product) {
//   const key = getProductCartKey(product);
//   return key ? Boolean(addingByKey.value[key]) : false;
// }

// --- TENANT / STORE RESOLUTION ---
function getTenantId() {
  const tenantDetails = appStore.tenantDetails || {};
  return (
    tenantDetails.tenantId ||
    tenantDetails.tenantID ||
    tenantDetails.id ||
    tenantDetails.tenant?.id ||
    null
  );
}

function getStoreId() {
  const tenantDetails = appStore.tenantDetails || {};
  return (
    tenantDetails.storeId ||
    tenantDetails.storeID ||
    tenantDetails.store?.id ||
    tenantDetails.defaultStoreId ||
    tenantDetails.defaultStoreID ||
    null
  );
}

function getTenantSubDomain(domainName) {
  const normalizedDomain =
    typeof domainName === "string" ? domainName.trim().toLowerCase() : "";
  if (!normalizedDomain) return null;
  const parts = normalizedDomain.split(".").filter(Boolean);
  if (parts.length >= 3 && parts[0] !== "www") return parts[0];
  return normalizedDomain;
}

function getTenantPayload(response) {
  if (!response) return null;
  if (response?.data?.data) return response.data.data;
  if (response?.data?.tenant) return response.data.tenant;
  if (response?.tenant) return response.tenant;
  if (response?.data) return response.data;
  return response;
}

function getListFromResponse(response, keys = []) {
  if (Array.isArray(response)) return response;
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(response?.[key])) return response[key];
  }
  return [];
}

async function resolveTenantAndStore() {
  const existingTenantId = getTenantId();
  const existingStoreId = getStoreId();

  if (existingTenantId) resolvedTenantId.value = String(existingTenantId);
  if (existingStoreId) resolvedStoreId.value = String(existingStoreId);
  if (resolvedTenantId.value && resolvedStoreId.value) return;

  const domainName = appStore.domainName || window.location.host || "";
  const tenantSubDomain =
    getTenantSubDomain(domainName) || appStore.tenantSubDomain;

  if (!domainName && !tenantSubDomain)
    throw new Error("Tenant domain is missing.");

  let tenantResponse;
  try {
    tenantResponse = await checkTenant(domainName);
  } catch (firstError) {
    if (!tenantSubDomain) throw firstError;
    tenantResponse = await checkTenant(tenantSubDomain);
  }

  const tenantData = getTenantPayload(tenantResponse);
  appStore.setTenantDetails(tenantData);

  const tenantId = getTenantId();
  const storeId = getStoreId();

  if (!tenantId)
    throw new Error("Tenant ID is missing. Please check tenant configuration.");
  if (!storeId)
    throw new Error(
      "Store ID is missing. Please check tenant/store configuration.",
    );

  resolvedTenantId.value = String(tenantId);
  resolvedStoreId.value = String(storeId);
}

// --- DATA FETCHING ---

// Merges filter groups from multiple categories by key, deduplicating
// their value lists so "All Collections" shows a sensible combined set.
function mergeFilterGroups(filterGroupLists) {
  const mergedByKey = new Map();

  for (const filterGroups of filterGroupLists) {
    for (const filter of filterGroups || []) {
      const key = String(filter?.key || filter?.name || "");
      if (!key) continue;

      if (!mergedByKey.has(key)) {
        mergedByKey.set(key, {
          key,
          name: filter?.name,
          display_name: filter?.display_name,
          values: [],
          seenValues: new Set(),
        });
      }

      const target = mergedByKey.get(key);
      const rawValues = Array.isArray(filter?.values) ? filter.values : [];

      for (const rawValue of rawValues) {
        const valueKey = String(
          rawValue && typeof rawValue === "object"
            ? rawValue.value ?? rawValue.display_name ?? ""
            : rawValue,
        );
        if (!valueKey || target.seenValues.has(valueKey)) continue;
        target.seenValues.add(valueKey);
        target.values.push(rawValue);
      }
    }
  }

  return Array.from(mergedByKey.values()).map((entry) => {
    // eslint-disable-next-line no-unused-vars
    const { seenValues, ...filter } = entry;
    return filter;
  });
}

async function fetchCategoryFilters(categoryId) {
  availableFilters.value = [];

  // Specific category selected: fetch its filters directly, as before.
  if (categoryId) {
    isFilterLoading.value = true;
    try {
      const filterResponse = await getFiltersByTenantAndStore({
        tenantId: resolvedTenantId.value,
        categoryId: String(categoryId),
      });
      availableFilters.value = getListFromResponse(filterResponse, [
        "filters",
        "items",
        "filterList",
      ]);
    } finally {
      isFilterLoading.value = false;
    }
    return;
  }

  // "All Collections": merge filters across every root category so
  // the filter panel isn't empty when nothing specific is selected.
  if (!categories.value.length) return;

  isFilterLoading.value = true;
  try {
    const rootCategoryIds = categories.value
      .map((category) => getCategoryId(category))
      .filter(Boolean);

    const filterResponses = await Promise.allSettled(
      rootCategoryIds.map((rootCategoryId) =>
        getFiltersByTenantAndStore({
          tenantId: resolvedTenantId.value,
          categoryId: String(rootCategoryId),
        }),
      ),
    );

    const filterGroupLists = filterResponses
      .filter((result) => result.status === "fulfilled")
      .map((result) =>
        getListFromResponse(result.value, ["filters", "items", "filterList"]),
      );

    availableFilters.value = mergeFilterGroups(filterGroupLists);
  } finally {
    isFilterLoading.value = false;
  }
}

async function fetchProducts(filtersPayload = []) {
  isProductsLoading.value = true;
  try {
    const productsResponse = await getProductsByTenantAndStore({
      tenantId: resolvedTenantId.value,
      storeId: resolvedStoreId.value,
      filters: Array.isArray(filtersPayload) ? filtersPayload : [],
      pageSize: 100,
      pageNumber: 1,
    });
    products.value = getListFromResponse(productsResponse, [
      "item_list",
      "products",
      "productList",
      "items",
    ]);
  } finally {
    isProductsLoading.value = false;
  }
}

// --- EVENT HANDLERS ---
async function handleCategoryClick(category, options = {}) {
  const { syncRoute = true } = options;
  const categoryId = getCategoryId(category);
  selectedCategoryId.value = categoryId;
  selectedFilters.value = {};
  mobileMenuOpen.value = false;

  if (syncRoute) {
    const currentQueryCategoryId = String(route.query.categoryId || "").trim();
    if (currentQueryCategoryId !== categoryId) {
      await router.replace({
        name: "shop",
        query: {
          ...route.query,
          categoryId,
        },
      });
    }
  }

  const [filterResult, productResult] = await Promise.allSettled([
    fetchCategoryFilters(categoryId),
    fetchProducts(),
  ]);

  if (
    filterResult.status === "rejected" &&
    productResult.status === "rejected"
  ) {
    const filterError = filterResult.reason;
    errorMessage.value =
      filterError?.response?.data?.statusMessage ||
      filterError?.message ||
      "Failed to fetch filter and product data.";
  }
}

// eslint-disable-next-line no-unused-vars
async function clearCategorySelection() {
  selectedCategoryId.value = "";
  availableFilters.value = [];
  selectedFilters.value = {};
  mobileMenuOpen.value = false;

  const nextQuery = { ...route.query };
  delete nextQuery.categoryId;
  await router.replace({
    name: "shop",
    query: nextQuery,
  });

  await Promise.allSettled([fetchCategoryFilters(""), fetchProducts()]);
}

async function applySelectedFilters() {
  const selectedPayload = getSelectedFiltersPayload();
  try {
    await fetchProducts(selectedPayload);
  } finally {
    showFiltersPanel.value = false;
  }
}

async function clearSelectedFilters() {
  selectedFilters.value = {};
  await fetchProducts([]);
}

async function handleProductClick(product) {
  const productId = getProductId(product);
  if (!productId) return;
  await router.push({ name: "product-detail", params: { productId } });
}

// function handleAddToCart(product) {
//   const key = getProductCartKey(product);
//   if (!key || isProductAdded(product) || isAddInProgress(product)) {
//     return;
//   }

//   addingByKey.value = {
//     ...addingByKey.value,
//     [key]: true,
//   };

//   cartStore
//     .addProductToCart(product)
//     .catch((error) => {
//       console.error("Add item API failed:", error);
//     })
//     .finally(() => {
//       addingByKey.value = {
//         ...addingByKey.value,
//         [key]: false,
//       };
//     });
// }

// --- BOOTSTRAP ---
async function loadShopData() {
  isBootstrapping.value = true;
  errorMessage.value = "";

  try {
    if (!appStore.baseUrl || !appStore.domainName) {
      appStore.initializeDomainConfig();
    }

    await resolveTenantAndStore();

    const categoryResponse = await getCategoriesByTenant(
      resolvedTenantId.value,
    );
    categories.value = getListFromResponse(categoryResponse, [
      "categories",
      "categoryList",
      "items",
    ]);

    const categoryFromQuery = String(route.query.categoryId || "").trim();
    const selectedByQuery = findCategoryById(
      categories.value,
      categoryFromQuery,
    );

    if (selectedByQuery) {
      await handleCategoryClick(selectedByQuery, { syncRoute: false });
    } else {
      // No category in the URL: default to "All Collections" and load
      // merged filters + all products, rather than picking the first category.
      await handleCategoryClick({ categoryId: "" }, { syncRoute: false });
    }
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to load shop data.";
  } finally {
    isBootstrapping.value = false;
  }
}

onMounted(() => {
  loadShopData();
});

watch(
  () => route.query.categoryId,
  async (nextCategoryId) => {
    const normalizedNextId = String(nextCategoryId || "").trim();

    if (!categories.value.length) return;

    if (!normalizedNextId) {
      if (selectedCategoryId.value) {
        selectedCategoryId.value = "";
        selectedFilters.value = {};
        await Promise.allSettled([fetchCategoryFilters(""), fetchProducts([])]);
      }
      return;
    }

    if (normalizedNextId === selectedCategoryId.value) return;

    const matchedCategory = findCategoryById(
      categories.value,
      normalizedNextId,
    );
    if (!matchedCategory) return;

    await handleCategoryClick(matchedCategory, { syncRoute: false });
  },
);
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap");
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.shop-page-root {
  --brand-blue: #003da5;
  --brand-yellow: #ffc72c;
  --charcoal-dark: #0a2240;
  --charcoal-body: #2d3748;
  --soft-bg-gray: #f8fafc;
  --border-light: rgba(0, 61, 165, 0.08);
  --muted-gray: #5f6368;
  --pure-white: #ffffff;
  background: var(--soft-bg-gray);
  color: var(--charcoal-body);
  min-height: 100vh;
}

/* HERO */
.hero-full-bleed-row {
  width: 100%;
  background: var(--brand-blue);
  display: block;
}
.shop-hero-banner {
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}
.hero-glow.one {
  width: 350px;
  height: 350px;
  background: rgba(255, 199, 44, 0.15);
  top: -100px;
  right: -50px;
}
.hero-glow.two {
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.06);
  bottom: -100px;
  left: -50px;
}
.banner-inner-container {
  width: 100%;
  max-width: 1240px;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}
.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 16px;
}
.crumb-link {
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  transition: color 0.2s;
}
.crumb-link:hover {
  color: var(--pure-white);
}
.breadcrumb-nav .separator {
  color: rgba(255, 255, 255, 0.4);
}
.breadcrumb-nav .active-node {
  color: rgba(255, 255, 255, 0.65);
}
.breadcrumb-nav .current-leaf {
  color: var(--brand-yellow);
  font-weight: 600;
}
.page-title-main {
  font-size: 44px;
  font-weight: 800;
  margin: 0 0 12px;
  color: var(--brand-yellow);
  letter-spacing: -0.5px;
}
.page-subtitle {
  margin: 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.85);
}

/* LAYOUT */
.content-full-bleed-row {
  width: 100%;
  background: transparent;
  display: block;
}
.shop-content {
  margin: 0 auto;
  padding: 30px 24px 50px;
  max-width: 1240px;
}
.shop-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 32px;
}

/* CARDS */
.section-card {
  background: var(--pure-white);
  border: 1px solid var(--border-light);
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 61, 165, 0.02);
}
.section-card-nested {
  background: var(--pure-white);
  border: 1px solid var(--brand-blue);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 28px;
  box-shadow: 0 4px 18px rgba(0, 61, 165, 0.04);
}

/* SECTION HEADING */
.section-heading-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 14px;
  margin-bottom: 18px;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--charcoal-dark);
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 0.75px;
}
.count-pill {
  background: rgba(0, 61, 165, 0.06);
  color: var(--brand-blue);
  border-radius: 20px;
  padding: 3px 10px;
  font-weight: 700;
  font-size: 12px;
}

/* MOBILE TOGGLE — hidden on desktop */
.mobile-category-bar {
  display: none;
}

/* CATEGORY PANEL */
.category-panel {
  align-self: start;
  position: sticky;
  top: 24px;
}
.category-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* CHIPS */
.category-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.18s ease;
  text-decoration: none;
  user-select: none;
}
.category-chip:hover {
  background: rgba(0, 61, 165, 0.04);
}
.category-chip.active {
  background: rgba(0, 61, 165, 0.07);
}
.category-chip.active .category-name {
  color: var(--brand-blue);
  font-weight: 700;
}

/* ROOT */
.root-chip .category-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--charcoal-dark);
}
.root-chip.active .category-name {
  color: var(--brand-blue);
  font-weight: 800;
}

/* LEVEL 1 */
.level-1-chip {
  padding-left: 20px;
}
.level-1-chip .category-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--charcoal-body);
}

/* LEVEL 2 */
.level-2-chip {
  padding-left: 36px;
}
.level-2-chip .category-name {
  font-size: 13px;
  font-weight: 400;
  color: var(--muted-gray);
}
.level-2-chip.active .category-name {
  color: var(--brand-blue);
  font-weight: 600;
}

.name-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}
.level-indicator {
  color: var(--muted-gray);
  font-size: 14px;
  line-height: 1;
}
.level-indicator.muted {
  color: #cbd5e0;
}
.chip-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.category-total-count-badge {
  background: #edf2f7;
  color: var(--muted-gray);
  border-radius: 20px;
  font-size: 11px;
  padding: 2px 8px;
  font-weight: 600;
  white-space: nowrap;
}

.tree-chevron {
  font-size: 16px;
  color: var(--muted-gray);
  transition: transform 0.22s ease;
  flex-shrink: 0;
}
.tree-chevron.open {
  transform: rotate(180deg);
}

.children-block {
  overflow: hidden;
}
.category-group {
  border-bottom: 1px solid #f1f5f9;
}
.category-group:last-child {
  border-bottom: none;
}

/* TREE TRANSITION */
.tree-expand-enter-active {
  transition: max-height 0.28s ease, opacity 0.22s ease;
  max-height: 600px;
}
.tree-expand-leave-active {
  transition: max-height 0.22s ease, opacity 0.18s ease;
}
.tree-expand-enter-from {
  max-height: 0;
  opacity: 0;
}
.tree-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* CATALOG HEADER */
.catalog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  margin-bottom: 28px;
}
.active-category {
  margin: 0;
  font-size: 15px;
  color: var(--charcoal-body);
}
.active-category strong {
  color: var(--brand-blue);
  font-weight: 700;
}
.count-summary {
  font-size: 13px;
  color: var(--muted-gray);
  display: block;
  margin-top: 2px;
}
.control-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* SORT SELECT */
.sort-select-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sort-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted-gray);
  white-space: nowrap;
}
.sort-select {
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: var(--pure-white);
  color: var(--charcoal-dark);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease;
}
.sort-select:hover,
.sort-select:focus {
  border-color: var(--brand-blue);
}

.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: var(--pure-white);
  color: var(--brand-blue);
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.filter-toggle-btn.active,
.filter-toggle-btn:hover {
  background: var(--brand-blue);
  color: var(--pure-white);
  border-color: var(--brand-blue);
}

.layout-toggle-group {
  display: flex;
  background: #edf2f7;
  padding: 4px;
  border-radius: 8px;
  gap: 2px;
}
.view-btn {
  border: none;
  background: transparent;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--muted-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 18px;
}
.view-btn.active {
  background: var(--pure-white);
  color: var(--brand-blue);
  box-shadow: 0 2px 6px rgba(0, 61, 165, 0.08);
}

/* FILTERS */
.mini-title {
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 800;
  color: var(--brand-blue);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.filter-groups {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.filter-group {
  background: #f8fafc;
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 18px;
}
.filter-group-title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  color: var(--charcoal-dark);
}
.filter-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  cursor: pointer;
  color: var(--charcoal-body);
}
.custom-checkbox {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid rgba(0, 61, 165, 0.3);
  border-radius: 4px;
  outline: none;
  background: var(--pure-white);
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: all 0.15s ease;
}
.custom-checkbox:checked {
  background: var(--brand-blue);
  border-color: var(--brand-blue);
}
.custom-checkbox:checked::before {
  content: "";
  width: 8px;
  height: 8px;
  background: var(--pure-white);
  border-radius: 1px;
}
.checkbox-label-text {
  font-weight: 500;
}
.filter-actions-footer {
  grid-column: 1 / -1;
  display: flex;
  gap: 12px;
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid #edf2f7;
}
.apply-filter-btn {
  background: var(--brand-blue);
  color: var(--pure-white);
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.clear-filter-btn {
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.15);
  color: var(--muted-gray);
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

/* PRODUCT GRID */
.product-cards-container.view-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.premium-product-card {
  background: var(--pure-white);
  border: 1px solid var(--border-light);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.015);
}
.premium-product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 61, 165, 0.06);
}

.product-image-wrap {
  position: relative;
  width: 100%;
  height: 240px;
  background: #fafafa;
  border-bottom: 1px solid var(--border-light);
}
.product-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.product-image-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-gray);
  font-size: 12px;
  font-weight: 600;
}
.badge-sale {
  position: absolute;
  top: 12px;
  left: 12px;
  background: var(--brand-blue);
  color: var(--brand-yellow);
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.75px;
  box-shadow: 0 2px 8px rgba(0, 61, 165, 0.15);
  z-index: 3;
}

.product-card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
}
.product-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--charcoal-dark);
  margin: 0 0 6px;
  line-height: 1.4;
}
.product-description {
  font-size: 13px;
  color: var(--muted-gray);
  margin: 0 0 12px;
  line-height: 1.5;
}
.product-prices {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 18px;
}
.price-chip.net {
  font-size: 18px;
  font-weight: 800;
  color: var(--brand-blue);
}
.price-chip.mrp {
  font-size: 13px;
  color: var(--muted-gray);
  text-decoration: line-through;
}

.action-btn-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.view-details-link {
  text-decoration: none;
  text-align: center;
  border: 1px solid rgba(0, 61, 165, 0.25);
  color: var(--brand-blue);
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.view-details-link:hover {
  background: rgba(0, 61, 165, 0.05);
}

.brand-movement-add-btn {
  background: var(--brand-yellow);
  border: 2px solid var(--brand-yellow);
  color: var(--charcoal-dark);
  border-radius: 50px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  width: 100%;
  box-shadow: 0 4px 12px rgba(255, 199, 44, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.25px;
}
.brand-movement-add-btn:hover {
  background: var(--brand-blue);
  border-color: var(--brand-blue);
  color: var(--pure-white);
  box-shadow: 0 4px 14px rgba(0, 61, 165, 0.3);
}
.brand-movement-add-btn.added,
.brand-movement-add-btn:disabled {
  background: #16a34a;
  border-color: #16a34a;
  color: var(--pure-white);
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.22);
  cursor: default;
}

/* LIST VIEW */
.product-cards-container.view-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.view-list .premium-product-card {
  flex-direction: row;
  height: 200px;
  align-items: center;
}
.view-list .product-image-wrap {
  width: 220px;
  height: 100%;
  border-bottom: none;
  border-right: 1px solid var(--border-light);
  flex-shrink: 0;
}
.view-list .product-card-body {
  padding: 24px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 30px;
}
.view-list .content-text-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.view-list .product-name {
  font-size: 18px;
  margin-bottom: 6px;
}
.view-list .product-description {
  margin-bottom: 10px;
}
.view-list .product-prices {
  margin-bottom: 0;
  gap: 12px;
}
.view-list .action-btn-wrapper {
  width: 200px;
  flex-shrink: 0;
}

/* STATUS */
.status-text {
  color: var(--muted-gray);
  margin: 6px 0;
  font-size: 14px;
}
.status-text.centered {
  text-align: center;
  padding: 60px 0;
}
.status-text.catalog-empty {
  padding: 50px;
  text-align: center;
}
.status-text.mini {
  font-size: 13px;
}
.error-text {
  color: #b91c1c;
  font-weight: 600;
}
.error-text.centered {
  text-align: center;
  padding: 60px 0;
}

/* RESPONSIVE */
@media (max-width: 1120px) {
  .product-cards-container.view-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 850px) {
  .shop-layout {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* MOBILE TOGGLE BAR */
  .mobile-category-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--pure-white);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    padding: 14px 18px;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 61, 165, 0.04);
    gap: 10px;
  }
  .mobile-cat-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: var(--charcoal-dark);
    white-space: nowrap;
  }
  .mobile-cat-label i {
    font-size: 16px;
  }
  .mobile-cat-selected {
    flex: 1;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    color: var(--brand-blue);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mobile-chevron {
    font-size: 20px;
    color: var(--muted-gray);
    flex-shrink: 0;
    transition: transform 0.25s ease;
  }
  .mobile-chevron.open {
    transform: rotate(180deg);
  }

  /* PANEL hidden by default on mobile */
  .category-panel {
    position: static;
    display: none;
  }
  .category-panel.mobile-open {
    display: block;
    animation: slideDown 0.26s ease forwards;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .catalog-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .control-actions {
    width: 100%;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .sort-select-wrap {
    width: 100%;
    order: -1;
  }
  .sort-select {
    flex: 1;
  }
}

@media (max-width: 600px) {
  .product-cards-container.view-grid {
    grid-template-columns: 1fr;
  }
  .view-list .premium-product-card {
    flex-direction: column;
    height: auto;
  }
  .view-list .product-image-wrap {
    width: 100%;
    height: 220px;
    border-right: none;
    border-bottom: 1px solid var(--border-light);
  }
  .view-list .product-card-body {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
  }
  .view-list .action-btn-wrapper {
    width: 100%;
  }
  .filter-groups {
    grid-template-columns: 1fr;
  }
  .page-title-main {
    font-size: 32px;
  }
}
</style>
